import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth as useCognitoAuth } from 'react-oidc-context'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'

const POST_LOGIN_KEY = 'postLoginNext'
const DEFAULT_POST_LOGIN_PATH = '/dashboard'

/**
 * Exchanges a Cognito OIDC session for a Learnly JWT, then routes to the app.
 * Lives at app root so Google callback works on `/` or `/auth/login`.
 */
const CognitoSessionBridge = () => {
  const cognitoAuth = useCognitoAuth()
  const { loginWithCognito, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const inFlight = useRef(false)

  useEffect(() => {
    if (!cognitoAuth.isAuthenticated || !cognitoAuth.user?.id_token) {
      inFlight.current = false
      return
    }

    if (isAuthenticated || inFlight.current) return

    inFlight.current = true
    const idToken = cognitoAuth.user.id_token

    const syncSession = async () => {
      try {
        await loginWithCognito(idToken)
        const next = sessionStorage.getItem(POST_LOGIN_KEY) || DEFAULT_POST_LOGIN_PATH
        sessionStorage.removeItem(POST_LOGIN_KEY)
        toast.success('Successfully logged in!')
        navigate(next, { replace: true })
      } catch (err) {
        inFlight.current = false
        const data = err.response?.data
        const audienceHint = data?.expected_audience && data?.token_audience
          ? ` (app expects ${data.expected_audience}, token has ${data.token_audience})`
          : ''
        toast.error((data?.error || 'Google sign-in failed. Please try again.') + audienceHint)
      }
    }

    syncSession()
    // loginWithCognito is stable enough for this flow; omit to avoid re-run loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cognitoAuth.isAuthenticated, cognitoAuth.user?.id_token, isAuthenticated, navigate])

  return null
}

export const storePostLoginPath = (path) => {
  if (path && path.startsWith('/')) {
    sessionStorage.setItem(POST_LOGIN_KEY, path)
  }
}

export default CognitoSessionBridge
